import { CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import { useEntityAttributes } from '~/data/attributes';
import {
  fetchEmployee,
  fetchEmployeeEmployment,
  useEmployee,
} from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import { useEmploymentDetails } from '~/data/employment/useEmploymentDetails';
import { useOrgUnitHRSupervisor } from '~/data/orgunits/useOrgUnitHRSupervisor';
import { ProposalType } from '~/data/proposals';
import { useGetProposals } from '~/data/proposals/useGetProposals';
import { useAuthentication } from '~/data/user/useAuthentication';
import { ScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/component';
import { getContracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/data';
import { useSchedulePendingEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useSchedulePendingEmployment';
import {
  DispatchReduxFetch,
  useInitializeCommonReduxData,
} from '~/hooks/initializeData';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';
import { LibraryComponentProviders } from '~/library/components/LibraryComponentProviders';
import { Providers } from '~/Providers';
import { AppDispatch } from '~/store';

export type ScheduleChanges_OnUpdateLegacy = {
  onUpdateLegacy: (updateReferences: string[]) => void;
};

export type ScheduleChangesDialogProps = ScheduleChanges_OnUpdateLegacy & {
  empId: number;
};

export const WrappedScheduleChangesDialog = (
  props: ScheduleChangesDialogProps,
) => {
  const { empId, onUpdateLegacy } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  const { userDetails, isAuthenticated } = useAuthentication();
  const { t } = useTranslation();
  const onOpen = useCallback(() => {
    setDialogOpen(true);
  }, [setDialogOpen]);
  const onClose = useCallback(() => {
    onUpdateLegacy(['empl', 'emplDetails', 'empPos', 'contracts']);
    setDialogOpen(false);
  }, [onUpdateLegacy]);

  const {
    requiredStatus: { isInitializing },
  } = useInitializeCommonReduxData({
    required: [
      DispatchReduxFetch.ApplicationConfig,
      DispatchReduxFetch.CompanyData,
      DispatchReduxFetch.AllEnums,
      DispatchReduxFetch.Languages,
      DispatchReduxFetch.AllEmployees,
      DispatchReduxFetch.AllOrgUnits,
      DispatchReduxFetch.AllCostCenters,
      DispatchReduxFetch.AllUnitPositions,
      DispatchReduxFetch.AllLegalEntities,
      DispatchReduxFetch.EmploymentDetailsAttributes,
    ],
  });

  //TODO: LIBRARY WRAPPER FOR EVERY REDUX STORE
  useEffect(() => {
    if (isAuthenticated && !!userDetails) {
      dispatch(fetchEmployee({ empId }));
      dispatch(fetchEmployeeEmployment({ empId }));
    }
  }, [dispatch, empId, isAuthenticated, userDetails]);

  const { getEmployeeById } = useAllEmployees();

  const uEmployee = useEmployee();
  const entityAttributes = useEntityAttributes();

  const portalIsLoading =
    !![uEmployee, entityAttributes].find(
      (dataObj) => dataObj.isLoading && !dataObj.hasData,
    ) || isInitializing;

  const upToDateUserData = uEmployee?.employee?.empBasicData?.fEmpId === empId;

  const fEmpEmplId = getEmployeeById(empId)?.fEmpEmplId;
  const { data: emplDetails, isLoading: employeeDetailsLoading } =
    useEmploymentDetails(empId, fEmpEmplId);
  const { isLoading: pendingDataLoading } = useSchedulePendingEmployment(
    empId,
    { revalidateOnFocus: false },
  );
  const { data: contracts, isLoading: contractsLoading } = useSWR(
    uEmployee?.employee?.empBasicData?.fEmpProfileId && upToDateUserData
      ? ['contracts', uEmployee.employee.empBasicData.fEmpProfileId]
      : null,
    () => getContracts(uEmployee.employee.empBasicData.fEmpProfileId),
    { revalidateOnFocus: false },
  );
  const { isLoading: proposalDetailsLoading } = useGetProposals({
    targetId: empId,
    proposalType: ProposalType.EMPLOYMENT_DETAILS_PROPOSAL,
  });
  //TODO FOR NOW, NEEDS TO DECIDE WHICH ORG UNIT
  const { isLoading: isHRSuperLoading } = useOrgUnitHRSupervisor({
    orgUnitId: uEmployee?.employee?.empPosition?.fInternalPositionUnit,
  });
  if (
    portalIsLoading ||
    pendingDataLoading ||
    employeeDetailsLoading ||
    contractsLoading ||
    proposalDetailsLoading ||
    isHRSuperLoading ||
    !upToDateUserData ||
    !emplDetails ||
    !contracts ||
    !isAuthenticated
  ) {
    return (
      <LegacyMuiButton startIcon={<CircularProgress size={20} />}>
        {t('laSchedule')}
      </LegacyMuiButton>
    );
  }
  return (
    <LibraryComponentProviders>
      <ScheduleChanges
        open={dialogOpen}
        handleOpenDialog={onOpen}
        handleCloseDialog={onClose}
        empId={empId}
      />
    </LibraryComponentProviders>
  );
};

export const renderScheduleChangesDialog = (
  containerId: string,
  props: ScheduleChangesDialogProps,
) => {
  ReactDOM.render(
    <Providers>
      <WrappedScheduleChangesDialog {...props} />
    </Providers>,
    document.getElementById(containerId),
  );
};
